import React from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  FormControl, 
  Select, 
  MenuItem, 
  Switch, 
  FormControlLabel,
  Button,
  IconButton,
  Tooltip,
  alpha
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';

/**
 * DocumentDetailsBar Component
 * Displays document date, VAT rate, items count, and price type toggle
 * Reusable across Delivery Notes, Sale Orders, and Invoices
 */
const DocumentDetailsBar = ({
  documentDate,
  onDateChange,
  vatPercentage,
  onVatChange,
  vatRates = [],
  itemsCount = 0,
  isTvaIncluded,
  onTvaToggle,
  dateLabel,
  showPriceTypeToggle = true,
  disableDateEdit = false,
  showCloseButton = false,
  onClose,
  showReopenButton = false,
  onReopen,
  status = 'Draft',
  showProfit = false,
  onProfitToggle,
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{
      display: 'flex',
      gap: 3,
      mb: 4,
      p: 3,
      background: '#f8f9fa',
      borderRadius: '8px',
      borderLeft: '4px solid #667eea',
      flexWrap: 'wrap',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      {/* Date Field */}
      <Box sx={{ flex: 1, minWidth: '150px' }}>
        <Typography 
          variant="caption" 
          fontWeight="600" 
          color="#2c3e50" 
          sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}
        >
          {dateLabel || t("document_date")}
        </Typography>
        <TextField
          type="date"
          value={documentDate || ''}
          onChange={(e) => onDateChange(new Date(e.target.value))}
          size="small"
          fullWidth
          disabled={disableDateEdit}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white',
              '& fieldset': { borderColor: '#e0e0e0' }
            }
          }}
        />
      </Box>

      {/* VAT Rate */}
      <Box sx={{ flex: 1, minWidth: '120px' }}>
        <Typography 
          variant="caption" 
          fontWeight="600" 
          color="#2c3e50" 
          sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}
        >
          {t("vat_rate")}
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={vatPercentage}
            onChange={(e) => onVatChange(parseFloat(e.target.value))}
            sx={{
              bgcolor: 'white',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' }
            }}
          >
            {vatRates.length > 0 ? (
              vatRates.map((rate) => (
                <MenuItem key={rate.Id} value={rate.VATPercentage}>
                  {rate.VATPercentage}%
                </MenuItem>
              ))
            ) : (
              <>
                <MenuItem value={0}>0%</MenuItem>
                <MenuItem value={6}>6%</MenuItem>
                <MenuItem value={12}>12%</MenuItem>
                <MenuItem value={21}>21%</MenuItem>
              </>
            )}
          </Select>
        </FormControl>
      </Box>

      {/* Items Count */}
      <Box sx={{ flex: 1, minWidth: '120px' }}>
        <Typography 
          variant="caption" 
          fontWeight="600" 
          color="#2c3e50" 
          sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}
        >
          {t("items")}
        </Typography>
        <Typography variant="h6" fontWeight="600" color="#667eea" sx={{ mt: 0.5 }}>
          {itemsCount}
        </Typography>
      </Box>

      {/* Price Type Toggle */}
      {showPriceTypeToggle && (
        <Box sx={{ flex: 1, minWidth: '140px' }}>
          <Typography 
            variant="caption" 
            fontWeight="600" 
            color="#2c3e50" 
            sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}
          >
            {t("price_type") || "Price Type"}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={isTvaIncluded}
                onChange={(e) => onTvaToggle(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#667eea',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#667eea',
                  },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                {t("tva_included") || "TVA Incl"}
              </Typography>
            }
            sx={{ mt: 0.5 }}
          />
        </Box>
      )}

      {/* Show Profit Toggle */}
      {onProfitToggle && (
        <Box sx={{ flex: 1, minWidth: '100px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', pb: 0.5 }}>
          <Tooltip title={showProfit ? t('hide_profit') || 'Hide Profit' : t('show_profit') || 'Show Profit'}>
            <IconButton
              size="small"
              onClick={() => onProfitToggle(!showProfit)}
              sx={{
                color: showProfit ? '#4caf50' : '#666',
                bgcolor: showProfit ? alpha('#4caf50', 0.1) : alpha('#666', 0.05),
                '&:hover': { bgcolor: alpha(showProfit ? '#4caf50' : '#666', 0.15) }
              }}
            >
              {showProfit ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
      
      {/* Close/Reopen Button */}
      {(showCloseButton || showReopenButton) && (
        <Box sx={{ flex: 1, minWidth: '150px', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', pb: 0.5 }}>
          {showCloseButton && status === 'Draft' && (
            <Button
              variant="contained"
              size="small"
              onClick={onClose}
              sx={{ 
                bgcolor: '#4caf50', 
                '&:hover': { bgcolor: '#388e3c' },
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                textTransform: 'none',
                px: 2
              }}
            >
              {t('close_order')}
            </Button>
          )}
          {showReopenButton && status === 'Closed' && (
            <Button
              variant="contained"
              size="small"
              onClick={onReopen}
              sx={{ 
                bgcolor: '#2196f3', 
                '&:hover': { bgcolor: '#1976d2' },
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                textTransform: 'none',
                px: 2
              }}
            >
              {t('reopen_order')}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DocumentDetailsBar;
